// ============================================================================
// AVATAR GROUP MANAGEMENT PAGE
// View, create, edit, and delete avatar groups
// ============================================================================

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Grid, Edit2, Trash2, ArrowLeft } from 'lucide-react';
import { AvatarGroup } from '../types';
import {
  getAvatarGroups,
  deleteAvatarGroup
} from '../services/avatarCatalogService';

export default function AvatarGroupManagement() {
  const navigate = useNavigate();
  const [groups, setGroups] = useState<AvatarGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const organizationId = 'current-org-id'; // TODO: Get from auth context

  useEffect(() => {
    loadGroups();
  }, []);

  const loadGroups = async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await getAvatarGroups(organizationId);
      setGroups(result.groups);
    } catch (err: any) {
      setError(err.message || 'Failed to load avatar groups');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (groupId: string, groupName: string) => {
    if (!window.confirm(`Are you sure you want to delete "${groupName}"? This cannot be undone.`)) {
      return;
    }

    try {
      await deleteAvatarGroup(groupId);
      alert(`Group "${groupName}" deleted successfully`);
      loadGroups();
    } catch (err: any) {
      alert(`Failed to delete group: ${err.message}`);
    }
  };

  return (
    <div className="p-8 max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-8">
        <div>
          <h2 className="text-3xl font-bold text-text-main">Avatar Groups</h2>
          <p className="text-text-sub text-sm mt-1">
            Create and manage reusable avatar collections for your workshops
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/avatars')}
            className="flex items-center gap-2 px-4 py-2.5 bg-white border border-slate-200 text-text-main font-medium rounded-xl hover:bg-slate-50 transition-all text-sm"
          >
            <ArrowLeft size={16} />
            Back to Catalog
          </button>
          <button
            onClick={() => navigate('/avatars/groups/create')}
            className="flex items-center gap-2 bg-primary hover:bg-primary/90 text-text-main font-bold px-5 py-2.5 rounded-xl transition-all shadow-md shadow-primary/20 text-sm"
          >
            <Plus size={16} />
            Create Group
          </button>
        </div>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="flex justify-center items-center py-20 text-text-sub">
          <div className="text-sm font-medium">Loading avatar groups...</div>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6">
          <p className="text-sm text-red-700"><strong>Error:</strong> {error}</p>
        </div>
      )}

      {/* Groups Grid */}
      {!loading && !error && (
        <>
          {groups.length === 0 ? (
            <div className="bg-slate-50 border-2 border-dashed border-slate-300 rounded-2xl py-16 px-6 text-center">
              <Grid size={48} className="mx-auto mb-5 text-slate-300" />
              <h3 className="text-lg font-semibold text-text-main mb-2">No avatar groups yet</h3>
              <p className="text-sm text-text-sub mb-6 max-w-md mx-auto">
                Create your first avatar group to quickly set up workshop sessions with predefined avatar collections
              </p>
              <button
                onClick={() => navigate('/avatars/groups/create')}
                className="px-5 py-2.5 bg-primary hover:bg-primary/90 text-text-main font-bold rounded-xl transition-all shadow-md shadow-primary/20 text-sm"
              >
                Create Your First Group
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {groups.map((group) => (
                <div
                  key={group.avatarGroupId}
                  className="bg-white border border-slate-200 rounded-2xl p-6 flex flex-col gap-4 hover:border-primary hover:shadow-lg hover:-translate-y-0.5 transition-all"
                >
                  {/* Header */}
                  <div>
                    <h3 className="text-lg font-semibold text-text-main mb-1">{group.name}</h3>
                    <p className="text-xs text-text-sub line-clamp-2">{group.description}</p>
                  </div>

                  {/* Stats */}
                  <div className="flex gap-4 p-3 bg-slate-50 rounded-xl">
                    <div className="flex-1">
                      <div className="text-[10px] font-semibold text-text-sub uppercase tracking-wider mb-1">Avatars</div>
                      <div className="text-xl font-bold text-text-main">{group.avatarSlots.length}</div>
                    </div>
                    <div className="flex-1">
                      <div className="text-[10px] font-semibold text-text-sub uppercase tracking-wider mb-1">Created</div>
                      <div className="text-sm font-semibold text-text-main">
                        {new Date(group.createdAt).toLocaleDateString()}
                      </div>
                    </div>
                  </div>

                  {/* Avatar Slots Preview */}
                  {group.avatarSlots.length > 0 && (
                    <div>
                      <div className="text-[10px] font-semibold text-text-sub uppercase tracking-wider mb-2">
                        Avatars in Group
                      </div>
                      <div className="flex gap-1.5 flex-wrap">
                        {group.avatarSlots.slice(0, 5).map((slot) => (
                          <span
                            key={slot.slotNumber}
                            className="px-2.5 py-1 bg-primary/10 text-primary-dark rounded text-[11px] font-semibold"
                          >
                            Slot {slot.slotNumber}
                          </span>
                        ))}
                        {group.avatarSlots.length > 5 && (
                          <span className="px-2.5 py-1 bg-slate-100 text-text-sub rounded text-[11px] font-semibold">
                            +{group.avatarSlots.length - 5} more
                          </span>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Actions */}
                  <div className="flex gap-2 pt-3 border-t border-slate-100 mt-auto">
                    <button
                      onClick={() => navigate(`/avatars/groups/${group.avatarGroupId}/edit`)}
                      className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2.5 bg-navy text-white rounded-xl text-xs font-semibold hover:bg-navy-dark transition-colors"
                    >
                      <Edit2 size={14} />
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(group.avatarGroupId, group.name)}
                      className="flex items-center justify-center gap-1.5 px-3 py-2.5 bg-red-50 text-red-600 border border-red-200 rounded-xl text-xs font-semibold hover:bg-red-100 transition-colors"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* Info Box */}
      {!loading && groups.length > 0 && (
        <div className="mt-10 bg-amber-50 border border-amber-200 rounded-xl p-5">
          <h3 className="text-sm font-semibold text-amber-800 mb-2">About Avatar Groups</h3>
          <p className="text-xs text-amber-700 leading-relaxed">
            Avatar groups are predefined collections of avatars that can be quickly selected when creating a new workshop session.
            They're perfect for standardizing avatar configurations across similar workshop types or for your frequently used setups.
          </p>
        </div>
      )}
    </div>
  );
}
